import React, { useCallback, useEffect, useMemo, useState } from 'react'
import withModal from '../../utils/hoc/withModal'
import { createConversionCampaignLink, fetchConversionCampaignLink } from '../../services/api/conversionCampaigns';
import { toast } from 'react-toastify';
import { copyToClipboard, extractOrigin, getErrorMessage } from '../../utils/helpers';
import withSlideUpModal from '../../utils/hoc/withSlideUpModalComponent';
import { ConversionCampaignLink } from '../../types/conversions';
import { fetchConversionCampaignLinksThunk } from '../../redux-stores/slices/conversionCampaignSlice';
import { AppDispatch, RootState } from '../../redux-stores/store';
import { useDispatch } from 'react-redux';
import imagePlaceholder from '../../assets/images/image-placeholder.png';
import { useSelector } from 'react-redux';

interface AddNewConversionCampaignLinkProps {
    campaignId: string,
    onClose: (refetch?: boolean) => void
}

const AddNewConversionCampaignLink: React.FC<AddNewConversionCampaignLinkProps> = ({ campaignId }) => {

    const dispatch: AppDispatch = useDispatch();
    const campaign = useSelector((state: RootState) => state.conversionCampaigns.items[campaignId!]?.campaign);

    const [url, setURL] = useState<string | null>(null);
    const links = useSelector((state: RootState) => state.conversionCampaigns.links[campaignId!]?.list);
    
    const [createdLinkTitle, setCreatedLinkTitle] = useState('');

    const [inputFieldDisabled, disableInputField] = useState(true);

    const [disableSubmission, setDisableSubmission] = useState(false);
    const [createdLink, setCreatedLink] = useState<ConversionCampaignLink | null>(null);

    const fetchCampaignLinks = useCallback(async (pageNumber: number, force: boolean = false) => {
        if (campaignId)
            await dispatch(fetchConversionCampaignLinksThunk({ campaignId, page: pageNumber, force }));
    }, [dispatch, campaignId]);

    const submit = async () => {
        if (!campaignId || !url) return false;

        setDisableSubmission(true);
        try {
            const link_id = await createConversionCampaignLink(campaignId, { url });
            const link = await fetchConversionCampaignLink(campaignId, link_id);
            setCreatedLink(link);
            toast.success("Link added successfully!", { autoClose: 3000, position: 'top-center' })
            fetchCampaignLinks(1, true);
            // onClose(true);
        } catch (error: any) {
            toast.error(getErrorMessage(error, "There was some issue, please try again.."), { autoClose: 3000, position: 'top-center' })
        } finally {
            setDisableSubmission(false);
        }
    }

    useEffect(() => {
        if (createdLink) {
            setCreatedLinkTitle(createdLink.title ?? createdLink.url);
        }
    }, [createdLink])

    const share = async () => {
        try {
            await navigator.share({
                title: "Hey! Checkout this link",
                text: createdLinkTitle ?? '',
                url: createdLink?.short_url
            })
        } catch (error) {
            
        }
    }

    useEffect(() => {
        if(url === null && campaign){
            if(campaign.deeplink_allowed){
                disableInputField(false);
                setURL('');
            }else{
                disableInputField(true);
                setURL(campaign.url);
            }
        }
    }, [url, campaign])

    return (
        <>
            <div className='create-earning-link p-3'>
                <div className="d-flex flex-column gap-2 mt-2">
                    <span className='create-earning-link-label'>{inputFieldDisabled ? "Create your earning link by converting the link below" : "Enter any product link to convert it into your earning link"}</span>
                    <input type="text" value={url ?? ''} disabled={inputFieldDisabled} onChange={(e) => setURL(e.target.value ?? '')} placeholder="Enter Link" className="create-earning-link-input" />
                </div>

                {
                    createdLink &&
                    <div className="conversion-campaign-link-card created-link mt-3">
                        <a href={createdLink.url} target='_blank' className="link-metadata">
                            <div className="link-thumbnail bg-image bgc-white" style={{ backgroundImage: `url(${createdLink.thumbnail_url ?? imagePlaceholder})` }}></div>
                            <div className="d-flex flex-column justify-content-center">
                                <div className="link-title">{createdLinkTitle.length > 50 ? createdLinkTitle.substring(0, 50) + '...' : createdLinkTitle}</div>
                                <div className="link-url">{extractOrigin(createdLink.url)}</div>
                            </div>
                        </a>
                        <button className='copy-link-btn' onClick={() => copyToClipboard(createdLink.short_url, "Short URL copied to your clipboard!")}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M5 9.1665C5 6.80948 5 5.63097 5.73223 4.89874C6.46447 4.1665 7.64298 4.1665 10 4.1665H12.5C14.857 4.1665 16.0355 4.1665 16.7678 4.89874C17.5 5.63097 17.5 6.80948 17.5 9.1665V13.3332C17.5 15.6902 17.5 16.8687 16.7678 17.6009C16.0355 18.3332 14.857 18.3332 12.5 18.3332H10C7.64298 18.3332 6.46447 18.3332 5.73223 17.6009C5 16.8687 5 15.6902 5 13.3332V9.1665Z" stroke="#007AFF" strokeWidth="1.25" />
                                <path d="M5 15.8332C3.61929 15.8332 2.5 14.7139 2.5 13.3332V8.33317C2.5 5.19047 2.5 3.61913 3.47631 2.64281C4.45262 1.6665 6.02397 1.6665 9.16667 1.6665H12.5C13.8807 1.6665 15 2.78579 15 4.1665" stroke="#007AFF" strokeWidth="1.25" />
                            </svg>
                            <span>Copy Link</span>
                        </button>
                        <button className='share-link-btn' onClick={() => share()}>
                            <span>Share Link</span>
                        </button>
                    </div>
                }

            </div>

            {
                !createdLink &&
                <>
                <a href={campaign?.url} target='_blank' className='text-center mb-2 d-block w-100 position-absolute' style={{bottom: createdLink ? '35px' : '86px'}}>Go To {campaign?.name} Website</a>
                <div className="sticky-footer pb-4 d-flex flex-column" style={{ bottom: '10px' }}>
                    <button disabled={disableSubmission || !url} onClick={() => submit()} className='create-earning-link-btn w-100'>Create Earning Link</button>
                </div>
                </>
            }
        </>
    )
}

export default withSlideUpModal(AddNewConversionCampaignLink, '', true)